import type { Middleware, Placement } from "@floating-ui/react-dom";
import { cx } from "@linaria/core";
import type { ReactNode, RefObject } from "react";
import { useEffect, useRef } from "react";
import { CSSTransition } from "react-transition-group";

import { TRANSITION_DURATION_MS } from "./constants";
import { useA11y } from "./hook.a11y";
import { useActiveState } from "./hook.active-state";
import { usePosition } from "./hook.position";
import * as styles from "./tooltip.styles";
import { resets } from "../../css-tokens/resets";
import { IsomorphicPortal } from "../isomorphic-portal";
import { useTransitionState } from "../../hooks/transition-state";

export { selectors as tooltipSelectors } from "./tooltip.styles";

export type TooltipProps = {
  className?: string;
  body?: ReactNode | ReactNode[];
  shortcut?: string;

  /**
   * choose how to apply aria-labelledby / aria-describedby on the target node
   */
  ariaType: "label" | "description" | undefined;

  /** forces tooltip to never be displayed */
  disabled?: boolean;

  /** forces tooltip to be displayed, even if the toggler ref isn't hovered */
  enabled?: boolean;

  /**
   * if enabled, then the tooltip will appear for the given amount of time
   * after the user taps it (or until he taps outside of it again)
   */
  showAfterTapMs?: number;
  disableShowAfterFocus?: boolean;

  placement: Placement;
  disableFallbackPlacements?: boolean;
  offsetPx?: number;

  /**
   * point to the DOM node which the tooltip describes.
   */
  containerRef?: React.RefObject<HTMLElement | SVGElement | null> | null;

  /**
   * optionally point to the DOM node which should trigger the tooltip on hover
   * or touch.
   */
  togglerRef?: React.RefObject<HTMLElement | SVGElement | null> | null;

  /**
   * @default "container"
   */
  pointAt?: "container" | "toggler";

  /**
   * In case an integration needs to override the default middlewares of the
   * overlay, then a custom middlewares array can be passed.
   *
   * NB: When provided the prop will override ALL default middlewares.
   */
  middlewares?: Middleware[];

  /**
   * specifies a parent layer that the tooltip should be mounted into, which
   * can help prevent cropping due to overflow settings on default parent
   * layers.
   */
  mountIn?: React.RefObject<HTMLElement | null> | HTMLElement | undefined;

  children: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    containerRef: RefObject<any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    togglerRef: RefObject<any>,
  ) => ReactNode | ReactNode[];
};

export function Tooltip(props: TooltipProps): React.JSX.Element {
  const { pointAt = "container" } = props;

  const internalContainerRef = useRef<HTMLElement>(null);
  const containerRef = props.containerRef ?? internalContainerRef;

  const internalTogglerRef = useRef<HTMLElement>(null);
  const togglerRef = props.togglerRef ?? internalTogglerRef;

  const labelRef = useRef<HTMLElement>(null);

  const active = useActiveState(props, containerRef, togglerRef);
  const transitionState = useTransitionState(active, TRANSITION_DURATION_MS);
  const { floating, arrowRef } = usePosition(
    props,
    pointAt === "container" ? containerRef : togglerRef,
    active || transitionState === "exit-active",
  );

  // keep a consistent aria-describedby applied to the toggler button
  const tooltipId = useA11y(props, containerRef, togglerRef);

  if ((!props.body && !props.shortcut) || props.disabled) {
    return <>{props.children(containerRef, togglerRef)}</>;
  }

  const target = props.mountIn || defaultContainer;

  return (
    <>
      {props.children(containerRef, togglerRef)}

      <IsomorphicPortal target={target}>
        <CSSTransition
          in={active}
          key={String(props.body)}
          timeout={TRANSITION_DURATION_MS}
          classNames={styles.tooltip}
          nodeRef={floating.refs.floating}
          mountOnEnter={props.ariaType === undefined}
          unmountOnExit={props.ariaType === undefined}
        >
          <div
            ref={floating.refs.setFloating}
            id={tooltipId}
            className={cx(
              resets.rootCls,
              styles.tooltip,
              props.className,
              active && "visible",
            )}
            data-placement={floating.placement}
            role="tooltip"
            style={floating.floatingStyles}
          >
            <span ref={labelRef} className={styles.label}>
              {props.body}
              {props.shortcut !== undefined && (
                <span className={styles.shortcut}>
                  {props.shortcut
                    .replace(/^cmd(\+|-)/i, "⌘")
                    .replace(/\+\+/, "+")
                    .replace(/-\+/, "+")
                    .replace(/\+-/, "-")}
                </span>
              )}
            </span>

            <span
              ref={arrowRef}
              className={styles.arrow}
              style={{
                left: floating.middlewareData.arrow?.x,
                top: floating.middlewareData.arrow?.y,
              }}
            />
          </div>
        </CSSTransition>
      </IsomorphicPortal>
    </>
  );
}

let defaultContainer: HTMLElement | undefined = undefined;

/**
 * allows configuration of a default tooltip container that will be used for
 * mounting tooltips, unless another mountIn prop is explicitly provided to the
 * <Tooltip> element
 */
export function configureTooltipContainer(node: HTMLElement | undefined): void {
  defaultContainer = node;
}
